import {
  QueryKey,
  UseBaseQueryOptions,
  useQuery,
  QueryClientConfig,
} from 'react-query';

import { AxiosResponse } from 'axios';

export interface IContentRangeQueryClientConfig extends QueryClientConfig {}

export type ContentRangeQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Pick<
  UseBaseQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>,
  | 'cacheTime'
  | 'enabled'
  | 'initialData'
  | 'meta'
  | 'onError'
  | 'onSettled'
  | 'onSuccess'
  | 'placeholderData'
  | 'refetchInterval'
  | 'refetchOnWindowFocus'
  | 'retry'
  | 'staleTime'
  | 'keepPreviousData'
>;

export type TContentRangeQueryFnData<T> = {
  data: T;
  headers?: AxiosResponse['headers'];
};

const formatResponseHeaders = (headers: AxiosResponse['headers'] | unknown) => {
  if (!headers || !headers?.['content-range']) return headers;

  const contentRangeArray = (headers['content-range'] || '').split('/');
  const range = contentRangeArray[0].split('-');

  const firstItemIndex = Number(range[0]);
  const lastItemIndex = Number(range[1]);
  const totalSize = Number(contentRangeArray[1]);

  return {
    ...headers,
    firstItemIndex,
    lastItemIndex,
    totalSize,
  };
};

export const useCreateContentRangeQuery = <
  TQueryFnData extends TContentRangeQueryFnData<
    TQueryFnData | unknown
  > = TContentRangeQueryFnData<unknown>,
  TError = unknown,
  TData extends TContentRangeQueryFnData<TQueryFnData | unknown> = TQueryFnData,
>(
  key: readonly unknown[],
  fetch: (params?: object | string) => Promise<TQueryFnData>,
  params?: object | string,
  options?: ContentRangeQueryOptions<TQueryFnData, TError, TData>,
) => {
  const query = useQuery<TQueryFnData, TError, TData>(
    [...key, params],
    () => fetch(params),
    options,
  );

  return {
    data: query.data?.data,
    headers: formatResponseHeaders(query.data?.headers),
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
};
